import { Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Workbook, Worksheet } from 'exceljs';
import { saveAs } from 'file-saver';

import { ApiService } from 'app/lib/api.service';
import { LogService } from 'app/lib/log.service';
import { ModalService } from 'app/lib/modal.service';
import { UserService } from 'app/lib/user.service';
import { AnalyticEvent } from 'app/lib/analytic-event';
import { User } from 'app/models/user';

/**
 * "My Data" — end-user data export.
 *
 * Calls the backend aggregate endpoint `GET /users/me/export` (see
 * web/docs/MY_DATA_API_SPEC.md) and renders the response into a single
 * .xlsx file. The backend resolves all per-domain detail (full
 * assessment list, thought-diary entries, action-plan answers,
 * demographic labels, etc.) so this file is mostly formatting.
 */
@Component({
    selector: 'app-my-data',
    templateUrl: './my-data.component.html',
    styleUrls: ['./my-data.component.scss'],
    standalone: false,
})
export class MyDataComponent implements OnInit {
    user: User;
    isPreparing = false;
    translation: { [key: string]: string } = {};

    constructor(
        private api: ApiService,
        private userService: UserService,
        private modalService: ModalService,
        private log: LogService,
        private translate: TranslateService
    ) {}

    ngOnInit(): void {
        this.user = this.userService.getUser();
        this.translate.stream('myData').subscribe((t: any) => {
            this.translation = t || {};
        });
    }

    downloadData(): void {
        if (this.isPreparing) {
            return;
        }
        this.isPreparing = true;
        this.log.event(AnalyticEvent.event.myDataDownloadRequested);

        this.api.get('users/me/export').subscribe({
            next: (response: ExportResponse) => {
                const payload = response?.data ?? ({} as ExportPayload);
                this.buildAndSaveWorkbook(payload)
                    .then(() => {
                        this.isPreparing = false;
                        this.log.event(
                            AnalyticEvent.event.myDataDownloadCompleted
                        );
                    })
                    .catch(err => this.handleFailure(err));
            },
            error: err => this.handleFailure(err),
        });
    }

    private async buildAndSaveWorkbook(data: ExportPayload): Promise<void> {
        const workbook = new Workbook();
        workbook.creator = 'WellTrack Boost';
        workbook.created = new Date();

        this.addProfileSheet(workbook, data.profile);

        this.addSheet(
            workbook,
            'Mood Checks',
            ['Date', 'Mood', 'Value', 'Place', 'Activity', 'Notes'],
            data.moodchecks,
            m => [
                this.formatDate(m.created),
                m.mood,
                m.value,
                m.place,
                m.activity,
                m.notes,
            ]
        );

        this.addAssessmentsSheet(workbook, data.assessments);

        this.addThoughtDiarySheet(workbook, data.thoughtDiaries);
        this.addActionPlansSheet(workbook, data.actionPlans);

        this.addSheet(
            workbook,
            'Activities',
            ['Title', 'Status', 'Start', 'End', 'Created'],
            data.activities,
            a => [
                a.title,
                a.status,
                this.formatDate(a.start),
                this.formatDate(a.end),
                this.formatDate(a.created),
            ]
        );

        this.addSheet(
            workbook,
            'Room for Positive',
            [
                'Date',
                'Proud Title',
                'Proud Description',
                'Grateful Title',
                'Grateful Description',
            ],
            data.roomForPositive,
            r => [
                this.formatDate(r.created),
                r.proudTitle,
                r.proudDescription,
                r.gratefulTitle,
                r.gratefulDescription,
            ]
        );

        this.addCdQuizSheet(workbook, data.cdQuizAttempts);

        this.addSheet(
            workbook,
            'Badges Earned',
            ['Key', 'Name', 'Category', 'Earned On'],
            data.badges,
            b => [b.key, b.name, b.category, this.formatDate(b.earnedOn)]
        );

        this.addSheet(
            workbook,
            'Video Series Progress',
            [
                'Series',
                'Series Category',
                'Video',
                'Status',
                'Time Completed (ms)',
                'View Count',
                'Updated',
            ],
            data.seriesProgress,
            s => [
                s.seriesName,
                s.seriesCategory,
                s.videoTitle,
                s.status,
                s.timeCompleted,
                s.viewCount,
                this.formatDate(s.updated),
            ]
        );

        this.addSheet(
            workbook,
            'Stories & Burning Questions',
            ['Type', 'ID', 'Title', 'Status', 'Time Completed (ms)', 'Updated'],
            data.voiceSeriesProgress,
            v => [
                v.kind,
                v.id,
                v.title,
                v.status,
                v.timeCompleted,
                this.formatDate(v.updated),
            ]
        );

        this.addSheet(
            workbook,
            'Daily Series History',
            ['Date', 'Seen At', 'Title', 'Content'],
            data.dailySeriesHistory,
            d => [
                this.formatDate(d.date),
                this.formatDate(d.seenAt),
                d.title,
                d.content,
            ]
        );

        this.addDemographicsSheet(workbook, data.demographics);
        this.addSurveySheet(workbook, 'NPS Survey', data.surveyResponses?.nps);
        this.addSurveySheet(
            workbook,
            'User Feedback Survey',
            data.surveyResponses?.userFeedback
        );

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        saveAs(blob, this.buildFileName());
    }

    private addProfileSheet(workbook: Workbook, profile?: ProfileEntry): void {
        const sheet = workbook.addWorksheet('Profile');
        sheet.columns = [
            { header: 'Field', key: 'field', width: 28 },
            { header: 'Value', key: 'value', width: 60 },
        ];
        sheet.addRows([
            { field: 'Full name', value: profile?.fullName ?? this.user?.fullName ?? '' },
            { field: 'Email', value: profile?.email ?? this.user?.email ?? '' },
            { field: 'User type', value: profile?.userType ?? this.user?.userType ?? '' },
            { field: 'Account created', value: this.formatDate(profile?.createdOn) },
            { field: 'Export generated', value: new Date().toISOString() },
        ]);
        this.styleHeaderRow(sheet);
    }

    private addAssessmentsSheet(
        workbook: Workbook,
        rows: AssessmentEntry[] = []
    ): void {
        const sheet = workbook.addWorksheet('Assessments');
        sheet.columns = [
            { header: 'Title', key: 'title', width: 35 },
            { header: 'Type', key: 'type', width: 18 },
            { header: 'Date', key: 'date', width: 22 },
            { header: 'Fields', key: 'fields', width: 80 },
        ];
        for (const row of rows ?? []) {
            const fieldsStr = Object.entries(row.fields ?? {})
                .map(([k, v]) => `${k}: ${v}`)
                .join(' | ');
            sheet.addRow([
                row.title,
                row.type,
                this.formatDate(row.created),
                fieldsStr,
            ]);
        }
        this.styleHeaderRow(sheet);
    }

    private addThoughtDiarySheet(
        workbook: Workbook,
        rows: ThoughtDiaryEntry[] = []
    ): void {
        const sheet = workbook.addWorksheet('Thought Diary');
        sheet.columns = [
            { header: 'Diary ID', key: 'id', width: 12 },
            { header: 'Status', key: 'status', width: 14 },
            { header: 'Event', key: 'event', width: 35 },
            { header: 'Plan', key: 'plan', width: 35 },
            { header: 'Feeling', key: 'feeling', width: 20 },
            { header: 'Intensity', key: 'intensity', width: 12 },
            { header: 'Thought', key: 'thought', width: 35 },
            { header: 'Belief', key: 'belief', width: 20 },
            { header: 'Thinking Styles', key: 'thinkingStyles', width: 25 },
            { header: 'Challenge', key: 'challenge', width: 35 },
            { header: 'Created', key: 'created', width: 22 },
        ];
        for (const diary of rows ?? []) {
            const entries = diary.entries?.length
                ? diary.entries
                : [{} as ThoughtDiaryStep];
            for (const e of entries) {
                sheet.addRow([
                    diary.id,
                    diary.status,
                    diary.event,
                    diary.plan,
                    e.feeling,
                    e.intensity,
                    e.thought,
                    e.belief,
                    e.thinkingStyles,
                    e.challenge,
                    this.formatDate(diary.created),
                ]);
            }
        }
        this.styleHeaderRow(sheet);
    }

    private addActionPlansSheet(
        workbook: Workbook,
        rows: ActionPlanEntry[] = []
    ): void {
        const sheet = workbook.addWorksheet('Action Plans');
        sheet.columns = [
            { header: 'Plan ID', key: 'id', width: 12 },
            { header: 'Title', key: 'title', width: 30 },
            { header: 'Language', key: 'language', width: 12 },
            { header: 'Action Plan Date', key: 'date', width: 22 },
            { header: 'Question', key: 'question', width: 40 },
            { header: 'Answer', key: 'answer', width: 50 },
            { header: 'Created', key: 'created', width: 22 },
        ];
        for (const plan of rows ?? []) {
            const questions = plan.questions?.length
                ? plan.questions
                : [
                      {
                          question: '(no questions)',
                          responses: [{ answer: '' }],
                      } as ActionPlanQuestion,
                  ];
            for (const q of questions) {
                const responses = q.responses?.length
                    ? q.responses
                    : [{ answer: '' }];
                for (const r of responses) {
                    sheet.addRow([
                        plan.id,
                        plan.title,
                        plan.language,
                        this.formatDate(plan.actionPlanDate),
                        q.question,
                        r.answer,
                        this.formatDate(plan.created),
                    ]);
                }
            }
        }
        this.styleHeaderRow(sheet);
    }

    private addCdQuizSheet(
        workbook: Workbook,
        rows: CdQuizAttempt[] = []
    ): void {
        const sheet = workbook.addWorksheet('Cognitive Distortion Quiz');
        sheet.columns = [
            { header: 'Attempt ID', key: 'id', width: 12 },
            { header: 'Score', key: 'score', width: 12 },
            { header: 'Date', key: 'date', width: 22 },
            { header: 'Answers', key: 'answers', width: 80 },
        ];
        for (const attempt of rows ?? []) {
            const answersStr = (attempt.answers ?? [])
                .map(a => `${a.optionId}=${a.value}`)
                .join(' | ');
            sheet.addRow([
                attempt.id,
                attempt.score,
                this.formatDate(attempt.created),
                answersStr,
            ]);
        }
        this.styleHeaderRow(sheet);
    }

    private addDemographicsSheet(
        workbook: Workbook,
        demographics?: DemographicsEntry
    ): void {
        const sheet = workbook.addWorksheet('Demographic Survey');
        sheet.columns = [
            { header: 'Question', key: 'question', width: 40 },
            { header: 'Answer', key: 'answer', width: 50 },
        ];

        const profile = demographics?.profile;
        if (profile) {
            const profileFields: Array<[string, unknown]> = [
                ['Age', profile.age],
                ['Date of Birth', this.formatDate(profile.dateOfBirth)],
                ['Gender', profile.gender],
                ['Ethnicity', profile.ethnicity],
                ['Year in Program', profile.yearInProgram],
                ['Referred by', profile.referred],
                ['Native Language', profile.nativeLanguage],
                ['Region', profile.region],
                ['Branch', profile.branch],
                ['Sports Team', profile.sportsTeam],
                ['Sports Team Type', profile.sportsTeamType],
                ['Clinic Role', profile.clinicRole],
                ['Clinic Name', profile.clinicName],
            ];
            for (const [label, value] of profileFields) {
                if (value !== null && value !== undefined && value !== '') {
                    sheet.addRow([label, value]);
                }
            }
        }

        for (const a of demographics?.formAnswers ?? []) {
            sheet.addRow([a.question, a.answer]);
        }
        this.styleHeaderRow(sheet);
    }

    private addSurveySheet(
        workbook: Workbook,
        name: string,
        rows?: SurveyAnswer[]
    ): void {
        if (!rows || rows.length === 0) {
            return;
        }
        const sheet = workbook.addWorksheet(name);
        sheet.columns = [
            { header: 'Question', key: 'question', width: 50 },
            { header: 'Answer', key: 'answer', width: 50 },
            { header: 'Submitted At', key: 'submittedAt', width: 22 },
        ];
        for (const a of rows) {
            sheet.addRow([a.question, a.answer, this.formatDate(a.submittedAt)]);
        }
        this.styleHeaderRow(sheet);
    }

    private addSheet<T>(
        workbook: Workbook,
        name: string,
        headers: string[],
        rows: T[] | undefined,
        toRow: (record: T) => unknown[]
    ): void {
        const sheet = workbook.addWorksheet(name);
        sheet.columns = headers.map(h => ({
            header: h,
            key: h.toLowerCase().replace(/\s+/g, '_'),
            width: 22,
        }));
        for (const r of rows ?? []) {
            sheet.addRow(toRow(r).map(v => v ?? ''));
        }
        this.styleHeaderRow(sheet);
    }

    private styleHeaderRow(sheet: Worksheet): void {
        const header = sheet.getRow(1);
        header.font = { bold: true };
        header.alignment = { vertical: 'middle' };
    }

    private formatDate(value: unknown): string {
        if (!value) {
            return '';
        }
        if (value instanceof Date) {
            return isNaN(value.getTime()) ? '' : value.toISOString();
        }
        const d = new Date(value as string | number);
        return isNaN(d.getTime()) ? String(value) : d.toISOString();
    }

    private buildFileName(): string {
        const safeName = (this.user?.fullName || 'welltrack-user')
            .replace(/[^a-zA-Z0-9-_]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .toLowerCase();
        const stamp = new Date().toISOString().slice(0, 10);
        return `welltrack-mydata_${safeName}_${stamp}.xlsx`;
    }

    private handleFailure(err: unknown): void {
        this.isPreparing = false;
        const detail = this.describeFailure(err);
        this.log.error('My Data export failed: ' + detail);
        const baseMessage =
            this.translation['errorMessage'] ||
            'We were unable to prepare your data file. Please try again later.';
        this.modalService.showAlert(
            this.translation['errorTitle'] || 'Error',
            detail ? `${baseMessage}\n\n${detail}` : baseMessage
        );
    }

    private describeFailure(err: unknown): string {
        if (!err || typeof err !== 'object') {
            return String(err ?? '');
        }
        const e = err as {
            status?: number;
            statusText?: string;
            message?: string;
            error?: any;
            url?: string;
        };
        const parts: string[] = [];
        if (typeof e.status === 'number') {
            parts.push(
                `HTTP ${e.status}${e.statusText ? ' ' + e.statusText : ''}`
            );
        }
        const body =
            typeof e.error === 'string'
                ? e.error
                : e.error?.message ?? e.error?.error ?? null;
        if (body) {
            parts.push(String(body));
        } else if (e.message) {
            parts.push(e.message);
        }
        if (e.url) {
            parts.push(e.url);
        }
        return parts.join(' — ');
    }
}

interface ExportResponse {
    data: ExportPayload;
    exportedAt?: string;
}

interface ExportPayload {
    profile?: ProfileEntry;
    moodchecks?: MoodcheckEntry[];
    assessments?: AssessmentEntry[];
    thoughtDiaries?: ThoughtDiaryEntry[];
    actionPlans?: ActionPlanEntry[];
    activities?: ActivityEntry[];
    roomForPositive?: RoomForPositiveEntry[];
    cdQuizAttempts?: CdQuizAttempt[];
    badges?: BadgeEntry[];
    seriesProgress?: SeriesProgressEntry[];
    voiceSeriesProgress?: VoiceSeriesProgressEntry[];
    dailySeriesHistory?: DailySeriesEntry[];
    demographics?: DemographicsEntry;
    surveyResponses?: { nps?: SurveyAnswer[]; userFeedback?: SurveyAnswer[] };
}

interface ProfileEntry {
    fullName?: string;
    email?: string;
    userType?: string;
    primaryOrganization?: unknown;
    createdOn?: string;
}

interface MoodcheckEntry {
    mood?: string;
    value?: number;
    place?: string;
    activity?: string;
    notes?: string;
    created?: string;
}

interface AssessmentEntry {
    title?: string;
    type?: string;
    fields?: Record<string, unknown>;
    created?: string;
}

interface ThoughtDiaryStep {
    feeling?: string;
    intensity?: number;
    thought?: string;
    belief?: string;
    thinkingStyles?: string;
    challenge?: string;
}

interface ThoughtDiaryEntry {
    id?: number;
    status?: string;
    event?: string;
    plan?: string;
    entries?: ThoughtDiaryStep[];
    created?: string;
}

interface ActionPlanResponse {
    fieldId?: number | string;
    groupId?: number | string;
    answer?: string;
}

interface ActionPlanQuestion {
    questionId?: number;
    question?: string;
    responses?: ActionPlanResponse[];
}

interface ActionPlanEntry {
    id?: number;
    title?: string;
    language?: string;
    actionPlanDate?: string;
    questions?: ActionPlanQuestion[];
    created?: string;
}

interface ActivityEntry {
    title?: string;
    status?: string;
    start?: string;
    end?: string;
    created?: string;
}

interface RoomForPositiveEntry {
    proudTitle?: string;
    proudDescription?: string;
    gratefulTitle?: string;
    gratefulDescription?: string;
    created?: string;
}

interface CdQuizAnswer {
    optionId?: string | number;
    value?: string | number;
}

interface CdQuizAttempt {
    id?: number;
    score?: number;
    answers?: CdQuizAnswer[];
    created?: string;
}

interface BadgeEntry {
    key?: string;
    name?: string;
    category?: string;
    earnedOn?: string;
}

interface SeriesProgressEntry {
    seriesKey?: string;
    seriesName?: string;
    seriesCategory?: string;
    videoId?: number;
    videoTitle?: string;
    status?: string;
    timeCompleted?: number | string;
    viewCount?: number;
    updated?: string;
}

interface VoiceSeriesProgressEntry {
    kind?: string;
    id?: number;
    title?: string;
    status?: string;
    timeCompleted?: number | string;
    updated?: string;
}

interface DailySeriesEntry {
    date?: string;
    seenAt?: string;
    title?: string;
    content?: string;
}

interface DemographicProfile {
    age?: number | string;
    gender?: string;
    ethnicity?: string;
    referred?: string | number;
    yearInProgram?: number | string;
    dateOfBirth?: string;
    sportsTeam?: string;
    sportsTeamType?: string;
    clinicRole?: string;
    clinicName?: string;
    nativeLanguage?: string;
    region?: string;
    branch?: string;
}

interface DemographicsEntry {
    profile?: DemographicProfile;
    formAnswers?: SurveyAnswer[];
}

interface SurveyAnswer {
    questionId?: number;
    question?: string;
    answer?: string | number;
    submittedAt?: string;
}
